import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  makeStyles,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import BackButton from '../../common/BackButton';
import { MoffTrainingRecord, TrainingMenu, DominantHand } from '../../../constants/Records';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(8),
  },
  userInfo: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  paper: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  formControl: {
    minWidth: 200,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

interface MoffTrainingRecordFormProps {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  menus: TrainingMenu[];
  currentRecord: MoffTrainingRecord | null;
  isEdit: boolean;
  executing: boolean;
  created: boolean;
  updated: boolean;
  onFetchRecord: (userId: string, trainingId: number) => void;
  onCreate: (userId: string, data: Partial<MoffTrainingRecord>, instructorName?: string) => void;
  onUpdate: (userId: string, startedAt: string, data: Partial<MoffTrainingRecord>) => void;
  onResetFlags: () => void;
}

const MoffTrainingRecordForm: React.FC<MoffTrainingRecordFormProps> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  menus,
  currentRecord,
  isEdit,
  executing,
  created,
  updated,
  onFetchRecord,
  onCreate,
  onUpdate,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  // フォーム状態
  // Unity アプリと同等のデータ構造に合わせる
  const [trainingId, setTrainingId] = useState<number>(1);
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  // count, average_angle, average_duration は summaries API に集計されるために必須
  // MoffBandなしの場合、Unityアプリでは count=10 が自動設定される
  const [count, setCount] = useState<number>(10);
  const [averageAngle, setAverageAngle] = useState<number>(0);
  const [averageDuration, setAverageDuration] = useState<number>(0);
  const [initialAngle, setInitialAngle] = useState<number>(0);
  const [targetAngle, setTargetAngle] = useState<number>(0);
  const [dominantHand, setDominantHand] = useState<DominantHand>('Right');
  const [withBand, setWithBand] = useState(false);
  const [instructorName, setInstructorName] = useState('');

  // 新規作成時のデフォルト値
  useEffect(() => {
    if (!isEdit) {
      const now = new Date();
      const formatted = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`;
      setStartedAt(formatted);
      // デフォルト値を設定
      setCount(10);
      setAverageAngle(0);
      setAverageDuration(0);
    }
  }, [isEdit]);

  // 編集時のレコード読み込み
  useEffect(() => {
    if (isEdit && currentRecord) {
      setTrainingId(currentRecord.training_id);
      setStartedAt(currentRecord.started_at);
      setEndedAt(currentRecord.ended_at || '');
      // すべてのフィールドは0をデフォルトとして設定（Unityアプリと同等）
      setCount(currentRecord.count ?? 10);
      setAverageAngle(currentRecord.average_angle ?? 0);
      setAverageDuration(currentRecord.average_duration ?? 0);
      setInitialAngle(currentRecord.initial_angle ?? 0);
      setTargetAngle(currentRecord.target_angle ?? 0);
      setDominantHand(currentRecord.dominant_hand || 'Right');
      setWithBand(currentRecord.with_band || false);
    }
  }, [isEdit, currentRecord]);

  // 作成/更新成功時の処理
  useEffect(() => {
    if (created || updated) {
      onResetFlags();
      history.push(`/institutions/${institutionId}/users/${userId}/records/mofftraining`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, updated]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Unity アプリと同等のデータ構造
    const data: Partial<MoffTrainingRecord> = {
      training_id: trainingId,
      started_at: startedAt,
      ended_at: endedAt || startedAt, // 終了日時がない場合は開始日時と同じ
      count: count,
      average_angle: averageAngle,
      average_duration: averageDuration,
      initial_angle: initialAngle,
      target_angle: targetAngle,
      // センサーデータ配列（手動入力では空配列）
      angles: [],
      absolute_angles: [],
      durations: [],
      dominant_hand: dominantHand,
      with_band: withBand,
    };

    if (isEdit) {
      onUpdate(uniqueId, startedAt, data);
    } else {
      onCreate(uniqueId, data, instructorName || undefined);
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  if (isEdit && executing && !currentRecord) {
    return (
      <div className={classes.root}>
        <BackButton />
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <BackButton />

      <Box className={classes.userInfo}>
        <Typography variant="subtitle2" color="textSecondary">
          施設名: {institutionName}
        </Typography>
        <Typography variant="h6">利用者: {userName} 様</Typography>
      </Box>

      <Typography variant="h5">{isEdit ? 'モフトレ記録 編集' : 'モフトレ記録 新規作成'}</Typography>

      <Paper className={classes.paper}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth className={classes.formControl}>
                <InputLabel>種目</InputLabel>
                <Select
                  value={trainingId}
                  onChange={e => setTrainingId(e.target.value as number)}
                  label="種目"
                  disabled={isEdit}
                >
                  {menus.map(menu => (
                    <MenuItem key={menu.id} value={menu.id}>
                      {menu.name} ({menu.type.toUpperCase()})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="開始日時"
                variant="outlined"
                fullWidth
                value={startedAt}
                onChange={e => setStartedAt(e.target.value)}
                placeholder="yyyy-mm-dd HH:MM:SS"
                disabled={isEdit}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="終了日時"
                variant="outlined"
                fullWidth
                value={endedAt}
                onChange={e => setEndedAt(e.target.value)}
                placeholder="yyyy-mm-dd HH:MM:SS"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="回数"
                variant="outlined"
                fullWidth
                type="number"
                value={count}
                onChange={e => setCount(parseInt(e.target.value, 10) || 0)}
                required
                helperText="※ 必須（MoffBandなしの場合は10回）"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="平均角度（度）"
                variant="outlined"
                fullWidth
                type="number"
                inputProps={{ step: '0.1' }}
                value={averageAngle}
                onChange={e => setAverageAngle(parseFloat(e.target.value) || 0)}
                helperText="センサー計測値（未入力時は0）"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="平均時間（秒）"
                variant="outlined"
                fullWidth
                type="number"
                inputProps={{ step: '0.1' }}
                value={averageDuration}
                onChange={e => setAverageDuration(parseFloat(e.target.value) || 0)}
                helperText="センサー計測値（未入力時は0）"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="初期角度（度）"
                variant="outlined"
                fullWidth
                type="number"
                inputProps={{ step: '0.1' }}
                value={initialAngle}
                onChange={e => setInitialAngle(parseFloat(e.target.value) || 0)}
                helperText="デフォルト: 0"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="目標角度（度）"
                variant="outlined"
                fullWidth
                type="number"
                inputProps={{ step: '0.1' }}
                value={targetAngle}
                onChange={e => setTargetAngle(parseFloat(e.target.value) || 0)}
                helperText="デフォルト: 0"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>利き手</InputLabel>
                <Select
                  value={dominantHand}
                  onChange={e => setDominantHand(e.target.value as DominantHand)}
                  label="利き手"
                >
                  <MenuItem value="Right">右</MenuItem>
                  <MenuItem value="Left">左</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Checkbox checked={withBand} onChange={e => setWithBand(e.target.checked)} color="primary" />}
                label="バンド使用"
              />
            </Grid>

            {!isEdit && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="指導者名"
                  variant="outlined"
                  fullWidth
                  value={instructorName}
                  onChange={e => setInstructorName(e.target.value)}
                />
              </Grid>
            )}
          </Grid>

          <Box className={classes.buttonContainer}>
            <Button variant="outlined" onClick={handleCancel} disabled={executing}>
              キャンセル
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={executing}>
              {executing ? <CircularProgress size={24} /> : isEdit ? '更新' : '作成'}
            </Button>
          </Box>
        </form>
      </Paper>
    </div>
  );
};

export default MoffTrainingRecordForm;
